import { createSingleton } from '@luna/utils';

import type { SSEEventBus } from './sse/SSEEventBusService';

const { get, set } = createSingleton<SSEEventBus>();

/**
 * Retrieves or creates a singleton SSE event bus.
 *
 * This function returns a singleton instance of the SSE event bus.
 * If the event bus doesn't exist, it creates one using the global window object.
 *
 * @returns {SSEEventBus} The SSE event bus instance.
 *
 * @example
 * const eventBus = getSseEventBus();
 *
 * // Listen for SSE events
 * eventBus.addEventListener('sse:someEvent', (event) => {
 *   console.log('Received SSE event:', event.detail);
 * });
 *
 * @remarks
 * The event bus is implemented using the window object, which means
 * it's globally accessible within the browser context.
 * This function ensures that only one instance of the event bus is used
 * throughout the application.
 */
export function getSseEventBus(): SSEEventBus {
    const value = get();

    if (value) {
        return value;
    }

    const eventBus = window;
    set(eventBus);

    return eventBus;
}
